import { CK } from '../core/calibrate.js';
import { applyH, homography } from '../core/linalg.js';
import { flatCam, renderer } from '../scene/context.js';
import { panelCorners, rightPanelDesc } from '../scene/panels.js';
import { requestRender } from '../state/bus.js';
import { $, S, clamp } from '../state/store.js';

/* ═══════════════════════════════════════════════════════════
   ON-SITE WARP  (CSS matrix3d from a 4-point homography)
   ═══════════════════════════════════════════════════════════ */
/* Forward warp maps render pixels onto the photo. Solving the same four points
   the other way round gives the inverse, which turns a pointer position on the
   photo back into a render pixel — and from there into a camera ray. So the
   gizmo works directly on the building. */
export let siteInv = null, siteDim = {w:1,h:1}, siteQuadH = 0;
/* The extra shrink overscanFit applies to fit the widened composite into the
   view. Exported because the gizmo sizes itself against the quad's ON-SCREEN
   height (scene/gizmo.js), and this is part of that height — without it the
   gizmo comes out this much too small and its handles get too fine to hit. */
export let siteFitK = 1;
/* The second panel of a corner screen, warped onto the same photograph by its
   own homography. Kept beside the first rather than folded into it, because
   every consumer has to know WHICH panel it is talking about — a pointer
   mapped through the wrong one lands somewhere plausible and wrong, which is
   the exact class of bug this codebase has already fixed twice. */
export let siteCornerH = null, siteCornerInv = null, siteCornerDim = { w:1, h:1 };
/* The tight, canvas-to-screen-quad homography itself, kept around so
   blitSiteGizmo can extrapolate a second warp from it — see there. */
export let siteH = null;
/* Zooming On-site is a preview aid: it scales the photo and the warped render
   together with a CSS transform, so the homography underneath is untouched and
   the export is unaffected. Layout sizes stay put, which is why blitSite can go
   on using clientWidth; only the pointer mapping has to divide the zoom back out. */
/* Min used to be 1 (never below "fit") on the assumption the CSS layout
   already fits the photo to the stage at 100% — true on a normal window,
   but a small/narrow stage can still leave the photo taller or wider than
   the visible area with no way to shrink it further. Letting zoom go below
   100% gives small screens a real way out. */
const SITE_ZOOM_MIN = 0.25, SITE_ZOOM_MAX = 8;
/* Overscan makes the composite bigger than the photograph, and the photograph
   already fills the stage — so without making room for it the extra render is
   simply pushed off the edge and clipped. It is genuinely rendered and
   genuinely on the canvas, and still unreachable: measured at 3× before this
   existed, a light's handle sat at client y = -141, above the top of the
   window. Shrink the whole composite to fit the widest thing it now contains
   and re-centre on that. At overscan 1 the bounds ARE the photo, so this
   resolves to the scale of 1 On-site has always used. */
function overscanFit(){
  const img = $('#siteImg'), cv = $('#siteCv');
  const dw = img ? img.clientWidth : 0, dh = img ? img.clientHeight : 0;
  const m = Math.max(1, S.siteOverscan || 1);
  if(!siteH || m === 1 || !dw || !dh || !cv || !cv.width) return { k: 1, dx: 0, dy: 0 };

  // the photo is always in view; the warped canvas may reach outside it
  let x0 = 0, y0 = 0, x1 = dw, y1 = dh;
  for(const [x,y] of [[0,0],[cv.width,0],[cv.width,cv.height],[0,cv.height]]){
    const q = applyH(siteH, x, y);
    if(!isFinite(q[0]) || !isFinite(q[1])) return { k: 1, dx: 0, dy: 0 };
    x0 = Math.min(x0, q[0]); y0 = Math.min(y0, q[1]);
    x1 = Math.max(x1, q[0]); y1 = Math.max(y1, q[1]);
  }
  const bw = Math.max(1, x1-x0), bh = Math.max(1, y1-y0);
  /* Whatever is hosting the wrap right now — #viewSite standalone, the split
     pane otherwise. Asking for #viewSite by name measured a box the mockup had
     left. */
  const host = $('#siteWrap')?.parentElement;
  const availW = Math.max(40, (host?.clientWidth || dw) - 24);
  const availH = Math.max(40, (host?.clientHeight || dh) - 24);
  const k = Math.min(availW/bw, availH/bh, 1);
  /* Where those bounds sit relative to the wrap's own centre, which is what
     transform-origin:center scales about — carried back out so the widened
     area lands in the middle of the view rather than hanging off one side. */
  const cx = (x0+x1)/2 - dw/2, cy = (y0+y1)/2 - dh/2;
  return { k, dx: -cx*k, dy: -cy*k, box: { x: x0, y: y0, w: bw, h: bh } };
}

/* Stretch the pointer-catching child out to whatever the composite covers.
   In the wrap's own coordinates, which is exactly the space overscanFit
   measured in, since the photo sits at its origin. */
function syncReach(box){
  const r = $('#siteReach');
  if(!r) return;
  if(!box){ r.style.display = 'none'; return; }
  r.style.display = 'block';
  r.style.left = box.x+'px'; r.style.top = box.y+'px';
  r.style.width = box.w+'px'; r.style.height = box.h+'px';
}

export function applySiteTransform(){
  const w = $('#siteWrap');
  if(!w) return;
  const z = S.siteZoom || 1, p = S.sitePan || {x:0,y:0};
  const f = overscanFit();
  siteFitK = f.k;
  syncReach(f.box);
  w.style.transformOrigin = 'center center';
  w.style.transform = `translate(${p.x + f.dx*z}px, ${p.y + f.dy*z}px) scale(${z * f.k})`;
  /* #zoomVal, not #siteZoomVal: this looked up an id that has never existed in
     the markup, so the readout it was keeping up to date was nothing at all.
     Flat now shares one on-canvas control with this view, and this is its
     percentage. */
  const zi = $('#zoomVal');
  if(zi) zi.textContent = Math.round(z*100)+'%';
}
export function setSiteZoom(z, anchor){
  const z0 = S.siteZoom || 1;
  const z1 = clamp(z, SITE_ZOOM_MIN, SITE_ZOOM_MAX);
  if(z1 === z0){
    // asking for 100% is a request to recentre, even if the scale is already 1
    if(z1 === 1) S.sitePan = {x:0, y:0};
    applySiteTransform();
    return;
  }
  const w = $('#siteWrap');
  if(w && anchor){
    // keep whatever sits under the cursor pinned while the scale changes
    const r = w.getBoundingClientRect();
    const cx = r.left + r.width/2, cy = r.top + r.height/2;
    const p = S.sitePan;
    const ux = anchor.x - cx + p.x, uy = anchor.y - cy + p.y;
    S.sitePan = { x: ux - (z1/z0)*(ux - p.x), y: uy - (z1/z0)*(uy - p.y) };
  }
  S.siteZoom = z1;
  if(z1 === 1) S.sitePan = {x:0, y:0};
  applySiteTransform();
  requestRender();
}
export function siteFit(){ S.siteZoom = 1; S.sitePan = {x:0,y:0}; applySiteTransform(); requestRender(); }

/* How far past the screen the working view reaches. NOT a setting and not
   persisted: scene/renderer.js's autoOverscan derives it from whatever is
   selected, so it is only ever as wide as the thing you are reaching for
   needs, and back to 1 the moment you select something on screen. */
export const OVERSCAN_MAX = 4;

/* The edge of the deliverable, drawn in CANVAS space so that the same warp
   which places the render puts this exactly on the screen quad — no second
   mapping to keep in step with the first. Everything outside is workspace: it
   exists to be reached, and it will never be exported, so it is dimmed rather
   than left looking like part of the piece. */
export function paintOverscanFrame(c2, cv, x, y, w, h, skip){
  c2.save();
  /* The dashed edge, and deliberately nothing else. There was a dim wash over
     everything outside it, meant to say "this will not export" — but the
     dashed rectangle already says that, and the wash brought its OWN
     rectangular edge with it: the boundary of the widened canvas, which
     corresponds to nothing in the scene or the photograph and simply reads as
     a mysterious translucent panel laid over the building. Two rectangles
     where one carries the meaning is worse than none. */
  const line = Math.max(2, cv.height * .004);
  c2.lineWidth = line;
  c2.setLineDash([line*3, line*2]);
  c2.strokeStyle = 'rgba(255,255,255,.9)';
  /* THREE SIDES ON A CORNER, never four. The two panels meet along one edge, so
     stroking each deliverable as a closed rectangle draws that edge twice —
     a dashed line straight down the middle of a picture that is meant to read
     as continuous. The edge is not an edge of the deliverable at all; it is the
     seam, and the seam is the thing the corner exists to hide.

     ui/calibrate-view.js's paintHandles already reached this conclusion for the
     panel outlines on the photo: fill as polygons, outline as OPEN polylines
     that skip the shared edge, and draw the hinge once by itself. */
  if(!skip){ c2.strokeRect(x, y, w, h); c2.restore(); return; }
  c2.beginPath();
  if(skip === 'right'){                       // panel one: everything but its right
    c2.moveTo(x + w, y); c2.lineTo(x, y); c2.lineTo(x, y + h); c2.lineTo(x + w, y + h);
  }else{                                      // panel two: everything but its left
    c2.moveTo(x, y); c2.lineTo(x + w, y); c2.lineTo(x + w, y + h); c2.lineTo(x, y + h);
  }
  c2.stroke();
  c2.restore();
}

/* Which panel a point on the photo falls in, in the photo's own display
   coordinates. Both marked quads are convex and share an edge, so the only
   ambiguity is a point exactly ON the seam — where it does not matter which
   answer comes back: the seam is ndc x = +1 of the left panel and x = −1 of
   the right, the same physical point either way. Measured landing on exactly
   −1 through the right panel's warp. */
function inQuad(q, x, y){
  let inside = false;
  for(let i = 0, j = 3; i < 4; j = i++){
    const [xi,yi] = q[i], [xj,yj] = q[j];
    if((yi > y) !== (yj > y) && x < (xj-xi)*(y-yi)/(yj-yi) + xi) inside = !inside;
  }
  return inside;
}
function markedQuad(keys){
  const img = $('#siteImg');
  const dw = img.clientWidth, dh = img.clientHeight;
  return keys.map(k => {
    const c = k === 'ctr' ? S.cornerPanel.outer.tr
            : k === 'cbr' ? S.cornerPanel.outer.br : S.corners[k];
    return [c.x*dw, c.y*dh];
  });
}
/* The quad the second panel was ACTUALLY warped onto, recorded by
   blitSiteCorner. Picking has to test the same rectangle the pixels went to,
   or a click lands in one panel and is decoded through the other's inverse —
   which is how it broke the moment the two stopped being the same quad. */
let siteCornerQuad = null;

/** 'left' | 'right' — which panel the photo point (dx,dy) belongs to. */
export function sitePanelAt(dx, dy){
  if(!S.cornerPanel) return 'left';
  if(inQuad(markedQuad(CK), dx, dy)) return 'left';
  const right = siteCornerQuad || markedQuad(['tr','ctr','cbr','br']);
  if(inQuad(right, dx, dy)) return 'right';
  return 'left';
}

/* THE PHOTO ARRIVES LATE, AND NOTHING USED TO NOTICE.

   Setting #siteImg.src is asynchronous. Opening a cloud project sets it and
   then renders synchronously — at which point the image has no intrinsic size
   yet, clientWidth is 0, and blitSite below returns without drawing. Nothing
   re-rendered when the decode finished, so On-site stayed empty until some
   unrelated event forced another pass; switching to Flat and back was the
   workaround people found.

   One listener, attached once at module load, because #siteImg is a fixed
   element in index.html rather than something rebuilt per project. It also
   covers the other paths that swap the photo — first upload, and the re-crop
   in calibrate-view — which had the same latent hole.

   Safe against looping: requestRender only sets a dirty flag, and drawing does
   not change src, so no second load event follows. */
{
  const siteImg = $('#siteImg');
  if (siteImg) siteImg.addEventListener('load', () => {
    /* Both, in this order: the transform depends on the size that has only
       just become known, and the render is what actually blits through it. */
    applySiteTransform();
    requestRender();
  });
}

export function blitSite(){
  const img = $('#siteImg'), cv = $('#siteCv');
  const dw = img.clientWidth, dh = img.clientHeight;
  if(!dw || !dh) return;
  const src = renderer.domElement;
  cv.width = src.width; cv.height = src.height;
  cv.style.width = src.width+'px';
  cv.style.height = src.height+'px';
  const c2 = cv.getContext('2d');
  c2.clearRect(0,0,cv.width,cv.height);

  /* With overscan the canvas holds a frustum `m`× wider than the screen, so the
     SCREEN occupies the middle 1/m of it. Solving the homography from that
     inner rectangle (rather than the canvas edges) onto the corner marks is
     what pins the deliverable exactly where it has always been while the extra
     render spills outward over the rest of the photograph — a homography is
     valid over the whole plane, not just inside the points it was solved from.

     siteInv is the same map inverted, and ui/interaction.js's pointerNDC
     normalises through siteDim, which is the FULL canvas — the same extent the
     widened camera's NDC covers. That is what keeps the pointer and the render
     in one frustum: both come from this one matrix and this one `m`. The
     version of gizmo widening that was reverted (see c0b4922) drew through a
     wide frustum and picked through a tight one; there is deliberately no
     second number here that could drift from the first. */
  const m = Math.max(1, S.siteOverscan || 1);
  const ix = cv.width/2, iy = cv.height/2, hx = cv.width/(2*m), hy = cv.height/(2*m);
  const s = [[ix-hx,iy-hy],[ix+hx,iy-hy],[ix+hx,iy+hy],[ix-hx,iy+hy]];

  /* The background is what the SCREEN shows, so under overscan it belongs
     inside the deliverable rectangle and nowhere else — left to the renderer's
     own clear it would flood the whole widened canvas and paint the building
     out of the photograph entirely. scene/renderer.js clears transparent while
     this is active precisely so the fill can be placed here instead, under the
     render rather than over it. */
  if(m > 1 && !S.bg.transparent){
    c2.fillStyle = S.bg.color;
    c2.fillRect(ix-hx, iy-hy, hx*2, hy*2);
  }
  c2.drawImage(src, 0, 0);
  // panel one's right edge IS the corner — see paintOverscanFrame
  if(m > 1) paintOverscanFrame(c2, cv, ix-hx, iy-hy, hx*2, hy*2, S.cornerPanel ? 'right' : null);

  const d = CK.map(k=>[S.corners[k].x*dw, S.corners[k].y*dh]);
  siteH = homography(s,d);
  siteInv = homography(d,s);
  siteDim = {w:cv.width, h:cv.height};

  /* On-site squeezes the whole render onto the screen quad inside the photo, so
     anything drawn at a fixed fraction of the render ends up much smaller on
     screen than the same thing in Orbit. Record the quad's height in display
     pixels so the gizmo can cancel that out and stay the same size to the eye. */
  const len = (a,b)=>Math.hypot(a[0]-b[0], a[1]-b[1]);
  siteQuadH = (len(d[0],d[3]) + len(d[1],d[2])) / 2;
  if(!siteH){ cv.style.transform='none'; return; }
  cv.style.transform =
    `matrix3d(${siteH[0]},${siteH[3]},0,${siteH[6]}, ${siteH[1]},${siteH[4]},0,${siteH[7]}, 0,0,1,0, ${siteH[2]},${siteH[5]},0,${siteH[8]})`;
  applySiteTransform();
}

/* A second, independent layer for the gizmo, warped with its own homography
   rather than reusing blitSite's. The gizmo pass renders through a frustum
   `margin`× wider than the screen (scene/renderer.js), so an object outside
   the frame still has a grabbable handle — but that means this canvas's
   pixels don't correspond to the same world rectangle blitSite's warp was
   built for; applying that warp directly would stretch it into a distorted
   blob (this shipped once — see git history/conversation for the visual).

   The fix: a homography is a projective map valid over the WHOLE plane, not
   just inside the four points it was solved from. So work out where this
   wider canvas's own corners would have landed under the TIGHT frustum's
   mapping (pure geometry — updateFlatCam's frustum bounds scale by margin
   around the screen's own centre, so a wide-canvas pixel's tight-canvas
   equivalent is just centre + (pixel-centre)*margin, exactly, independent
   of the eye's off-axis shear — see the derivation in conversation), run
   THOSE through siteH to place them in photo space, then solve a fresh
   homography from this canvas's real corners to those points. */
/* HALF THE PLANE EACH, SPLIT AT THE HINGE.

   Both gizmo layers cover the whole photograph — that is deliberate, it is how
   a handle stays reachable outside the frame. But each is a copy of the SAME
   gizmo render warped by a different panel's homography, so drawn in full they
   are two pictures of one gizmo in two places: a doubled, broken-looking
   outline over the building. (Reported as "weird artifacts when I click the
   object", and it is exactly that.)

   The panels meet along one line, and every point of the photo is on one side
   of it or the other. So each layer keeps its own side and the two halves meet
   exactly — no blending, no seam arithmetic. In CANVAS space that line is
   simply the vertical edge of this layer's own deliverable rectangle: the RIGHT
   edge for panel one, the LEFT for panel two. Both maps carry it to the same
   photo line by construction, because the shared marks are literally the same
   two points in both quads.

   Clipping here rather than by cutting the gizmo geometry: a handle that
   crosses the hinge has to continue on the other panel, drawn through THAT
   panel's frustum, which is what the second pass already renders. Half from
   each is a continuous handle; that is the same reasoning that added the second
   pass in the first place. */
function halfAtHinge(c2, src, cv, side){
  const m = Math.max(1, S.siteOverscan || 1);
  /* Rounded to a whole pixel, and rounded as a HALF-WIDTH from the centre so
     both layers round identically — cut at a fraction, drawImage resamples
     across the boundary and leaves a sliver of the neighbour's handles on the
     wrong side of the hinge. Measured: seven pixels of it. */
  const k = Math.round(cv.width/(2*m));
  const x = cv.width/2 + (side === 'left' ? +k : -k);
  const [sx, sw] = side === 'left' ? [0, x] : [x, cv.width - x];
  if(sw <= 0) return;
  c2.drawImage(src, sx, 0, sw, cv.height, sx, 0, sw, cv.height);
}

export function blitSiteGizmo(margin, splitAtHinge){
  const gcv = $('#siteGizmoCv');
  if(!gcv) return;
  if(!siteH){ gcv.style.display = 'none'; return; }
  const src = renderer.domElement;
  gcv.width = src.width; gcv.height = src.height;
  gcv.style.width = src.width+'px';
  gcv.style.height = src.height+'px';
  const c2 = gcv.getContext('2d');
  c2.clearRect(0,0,gcv.width,gcv.height);
  // whole plane when this is the only panel; its own half when it is not
  if(splitAtHinge) halfAtHinge(c2, src, gcv, 'left');
  else c2.drawImage(src, 0, 0);

  const w = gcv.width, h = gcv.height;
  const tightEquivalent = (x,y) => [ w/2 + (x-w/2)*margin, h/2 + (y-h/2)*margin ];
  const wideCorners = [[0,0],[w,0],[w,h],[0,h]];
  const photoCorners = wideCorners.map(([x,y]) => {
    const [tx,ty] = tightEquivalent(x,y);
    return applyH(siteH, tx, ty);
  });
  const H2 = homography(wideCorners, photoCorners);
  if(!H2){ gcv.style.display = 'none'; return; }
  gcv.style.display = '';
  gcv.style.transform =
    `matrix3d(${H2[0]},${H2[3]},0,${H2[6]}, ${H2[1]},${H2[4]},0,${H2[7]}, 0,0,1,0, ${H2[2]},${H2[5]},0,${H2[8]})`;
}

/* Split's flat-preview pane (scene/renderer.js's drawSplit) — a plain copy
   of whatever was just rendered, no homography/CSS warp involved, since
   there's no photo to map onto here (Split only exists in the no-photo
   path — see ui/chrome.js's setView). #cvSplitR just sits at 100%/100% of
   its own box (styles/app.css's .fitbox canvas), so matching its pixel
   buffer to the source is all that's needed. */
/* Copy the live canvas into another one. Split's second pane and a corner
   screen's second panel both need it, for the same reason: only one WebGL
   canvas exists, so any pane that is not rendered last has to be taken off it
   before the next pass overwrites it. */
export function blitInto(sel){
  const cv = $(sel);
  if(!cv) return;
  const src = renderer.domElement;
  cv.width = src.width; cv.height = src.height;
  const c2 = cv.getContext('2d');
  c2.clearRect(0,0,cv.width,cv.height);
  c2.drawImage(src, 0, 0);
}

/* The corner screen's second panel, composited onto the same photograph as an
   independent warped layer — the same technique blitSiteGizmo already uses for
   the gizmo. Its quad is the second panel's four marks, whose first two ARE
   the first panel's right edge, so the two land exactly edge to edge with no
   seam arithmetic of their own. */
/* Where the second panel ACTUALLY lands, as panel one's own mapping sees it.

   A corner pin is a 4-point homography, and the only question is which four
   points. Pinning the second panel to its MARKS puts its outline exactly on
   the building — and pins an image rendered for a 90° panel onto a quad that
   is not where a 90° panel projects, so the content arrives mis-registered and
   the whole of that error surfaces at the seam, growing with depth because a
   plane-to-plane map carries no depth.

   This is the other choice. Panel two's four corners are pushed through panel
   ONE's pipeline — its camera, then siteH, the homography already fitted to
   panel one's own marks — so both panels end up sharing a single global
   mapping. The seam is then exact by construction rather than by fitting:
   there is one map, so there is nothing for two maps to disagree about.

   What it costs is the outline. Where the marks and the projection differ, the
   second panel's edge sits where the geometry says rather than where it was
   marked. That trade is deliberate and is the user's to make; S.siteSeamExact
   is the switch, and the export never comes through here either way. */
function projectedCornerQuad(){
  const desc = rightPanelDesc();
  if(!desc || !siteH) return null;
  /* panelCorners returns TL TR BR BL in the panel's own frame, and the second
     panel's local −X edge IS the hinge (scene/panels.js) — so this comes out
     in the same order markedQuad(['tr','ctr','cbr','br']) does: hinge-top,
     outer-top, outer-bottom, hinge-bottom. They have to match, or the warp is
     applied with two of its corners swapped. */
  const out = [];
  for(const p of panelCorners(desc)){
    const v = p.clone().project(flatCam);
    if(!Number.isFinite(v.x) || !Number.isFinite(v.y)) return null;
    const cx = (v.x*0.5 + 0.5) * siteDim.w;
    const cy = (-v.y*0.5 + 0.5) * siteDim.h;
    const q = applyH(siteH, cx, cy);
    if(!q || !Number.isFinite(q[0]) || !Number.isFinite(q[1])) return null;
    out.push([q[0], q[1]]);
  }
  return out;
}

export function blitSiteCorner(){
  const img = $('#siteImg'), cv = $('#siteCornerCv');
  if(!cv) return;
  if(!S.cornerPanel){ cv.style.display = 'none'; siteCornerH = siteCornerInv = siteCornerQuad = null; return; }
  const dw = img.clientWidth, dh = img.clientHeight;
  const src = renderer.domElement;
  if(!dw || !dh || !src.width){ cv.style.display = 'none'; return; }
  cv.width = src.width; cv.height = src.height;
  cv.style.width = src.width+'px';
  cv.style.height = src.height+'px';
  const c2 = cv.getContext('2d');
  c2.clearRect(0,0,cv.width,cv.height);

  /* The same overscan arithmetic blitSite does, and it has to be here too: the
     corner camera is handed the identical margin, so with the frustum widened
     the panel occupies the middle 1/m of this canvas and NOT its edges.
     Mapping the full canvas onto the quad instead stretched the second panel by
     the margin — measured, the shared hinge landed at 0.5419 of the photo
     instead of 0.5000, which is the corner visibly coming apart. */
  const m = Math.max(1, S.siteOverscan || 1);
  const ix = cv.width/2, iy = cv.height/2;
  const hx = cv.width/(2*m), hy = cv.height/(2*m);
  if(m > 1 && !S.bg.transparent){
    c2.fillStyle = S.bg.color;
    c2.fillRect(ix-hx, iy-hy, hx*2, hy*2);
  }else if(!S.bg.transparent){
    c2.fillStyle = S.bg.color; c2.fillRect(0,0,cv.width,cv.height);
  }
  c2.drawImage(src, 0, 0);
  // and panel two's left edge is the same corner
  if(m > 1) paintOverscanFrame(c2, cv, ix-hx, iy-hy, hx*2, hy*2, 'left');

  const s = [[ix-hx,iy-hy],[ix+hx,iy-hy],[ix+hx,iy+hy],[ix-hx,iy+hy]];
  /* Falls back to the marks whenever the projection cannot be formed — no
     homography yet, a degenerate camera — so this can never leave the panel
     unplaced, only differently placed. */
  const d = (S.siteSeamExact === true && projectedCornerQuad())
    || markedQuad(['tr','ctr','cbr','br']);
  siteCornerQuad = d;         // picking must test the quad the pixels went to
  siteCornerH = homography(s,d);
  siteCornerInv = homography(d,s);
  siteCornerDim = { w: cv.width, h: cv.height };
  if(!siteCornerH){ cv.style.display = 'none'; return; }
  cv.style.display = '';
  cv.style.transform =
    `matrix3d(${siteCornerH[0]},${siteCornerH[3]},0,${siteCornerH[6]}, ${siteCornerH[1]},${siteCornerH[4]},0,${siteCornerH[7]}, 0,0,1,0, ${siteCornerH[2]},${siteCornerH[5]},0,${siteCornerH[8]})`;
}

/* The gizmo, warped onto the SECOND panel. An object at the corner is visible
   through both panels, so its handles are too — and each half has to be drawn
   through that panel's own frustum and warped by that panel's own homography.
   With only the first layer the gizmo stopped dead at the hinge, cut in half. */
export function blitSiteGizmoCorner(){
  const gcv = $('#siteGizmoCornerCv');
  if(!gcv) return;
  if(!S.cornerPanel || !siteCornerH){ gcv.style.display = 'none'; return; }
  const src = renderer.domElement;
  gcv.width = src.width; gcv.height = src.height;
  gcv.style.width = src.width+'px';
  gcv.style.height = src.height+'px';
  const c2 = gcv.getContext('2d');
  c2.clearRect(0,0,gcv.width,gcv.height);
  halfAtHinge(c2, src, gcv, 'right');
  gcv.style.display = '';
  gcv.style.transform =
    `matrix3d(${siteCornerH[0]},${siteCornerH[3]},0,${siteCornerH[6]}, ${siteCornerH[1]},${siteCornerH[4]},0,${siteCornerH[7]}, 0,0,1,0, ${siteCornerH[2]},${siteCornerH[5]},0,${siteCornerH[8]})`;
}

/* Split's right pane, by its old name — the only caller that predates there
   being more than one thing to blit into. */
export const blitSplitFlat = () => blitInto('#cvSplitR');

/* Nothing selected (or the corner solve is degenerate) — don't leave a stale
   handle from whatever was last drawn here. */
export function hideSiteGizmo(){
  const gcv = $('#siteGizmoCv');
  if(gcv) gcv.style.display = 'none';
  const ccv = $('#siteGizmoCornerCv');
  if(ccv) ccv.style.display = 'none';
}
